import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import useEmblaCarousel from "embla-carousel-react";
import { Star, Calendar, Play, ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";
import api, { BACKEND } from "@/lib/api";
import { useCms } from "@/lib/cms";

const resolve = (u) => (!u ? "" : u.startsWith("http") ? u : `${BACKEND}${u}`);
const MONTHS = ["", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Shared cover resolver — same priority as Achievements.jsx
function getCover(item) {
  if (item.featured_image) return item.featured_image;
  if (item.thumbnail) return item.thumbnail;
  if (item.cover_image) return item.cover_image;
  if (item.images?.[0]) return item.images[0];
  if (item.youtube_urls?.[0]) {
    const m = item.youtube_urls[0].match(
      /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/
    );
    if (m) return `https://img.youtube.com/vi/${m[1]}/maxresdefault.jpg`;
  }
  return null;
}

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] } },
};

function getYouTubeId(url) {
  const m = (url || "").match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/);
  return m ? m[1] : null;
}

// YouTube thumbnail with quality fallback: maxresdefault → hqdefault → mqdefault → default
function YtImg({ videoId, alt, className }) {
  const chain = ["maxresdefault", "hqdefault", "mqdefault", "default"];
  const [qi, setQi] = useState(0);
  return (
    <img
      src={`https://img.youtube.com/vi/${videoId}/${chain[qi]}.jpg`}
      alt={alt || ""}
      className={className}
      loading="lazy"
      onError={() => setQi(q => Math.min(q + 1, chain.length - 1))}
    />
  );
}

function DriveVideoModal({ url, onClose }) {
  // Convert drive.google.com/file/d/ID/view → embed URL
  const embedUrl = url.replace(/\/view.*$/, "/preview").replace("drive.google.com/file/d/", "drive.google.com/file/d/");
  useEffect(() => {
    const h = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-4xl relative" onClick={e => e.stopPropagation()}>
        <button onClick={onClose} className="absolute -top-10 right-0 text-white/70 hover:text-white"><span className="text-2xl">✕</span></button>
        <div className="rounded-2xl overflow-hidden bg-black aspect-video">
          <iframe src={embedUrl} className="w-full h-full" allow="autoplay" allowFullScreen title="Drive video" />
        </div>
      </div>
    </div>
  );
}

function YoutubeModal({ url, onClose }) {
  const vid = getYouTubeId(url);
  useEffect(() => {
    const h = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-4xl relative" onClick={e => e.stopPropagation()}>
        <button onClick={onClose} className="absolute -top-10 right-0 text-white/70 hover:text-white"><span className="text-2xl">✕</span></button>
        <div className="rounded-2xl overflow-hidden bg-black aspect-video">
          <iframe src={`https://www.youtube.com/embed/${vid}?autoplay=1&rel=0&modestbranding=1&playsinline=1`} className="w-full h-full" allow="autoplay; encrypted-media" allowFullScreen title="YouTube video" />
        </div>
      </div>
    </div>
  );
}

export default function AchievementsSection() {
  const { home } = useCms();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [videoModal, setVideoModal] = useState(null); // { type: 'youtube'|'drive', url }

  useEffect(() => {
    api.get("/achievements").then(r => {
      const data = (r.data || []).filter(a => a.published);
      setItems(data.slice(0, 12));
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: "start",
    slidesToScroll: 1,
    containScroll: "trimSnaps",
  });

  // Auto-scroll — only when items present
  useEffect(() => {
    if (!emblaApi || items.length <= 1) return;
    let timer;
    let paused = false;
    const root = emblaApi.rootNode();
    const play = () => { timer = setInterval(() => { if (!paused) emblaApi.scrollNext(); }, 4000); };
    const onEnter = () => { paused = true; };
    const onLeave = () => { paused = false; };
    root.addEventListener("mouseenter", onEnter);
    root.addEventListener("mouseleave", onLeave);
    root.addEventListener("touchstart", onEnter, { passive: true });
    root.addEventListener("touchend", onLeave);
    play();
    return () => {
      clearInterval(timer);
      root.removeEventListener("mouseenter", onEnter);
      root.removeEventListener("mouseleave", onLeave);
      root.removeEventListener("touchstart", onEnter);
      root.removeEventListener("touchend", onLeave);
    };
  }, [emblaApi, items.length]);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  // CMS-editable heading and description — never hardcoded
  const heading = home?.achievements_heading || "Achievements";
  const description = home?.achievements_description || "Celebrating the milestones, recognitions, teacher achievements and foundation activities throughout the year.";

  function handleCardClick(item) {
    // Priority: YouTube → Drive → open achievements page
    if (item.youtube_urls?.length > 0) {
      setVideoModal({ type: "youtube", url: item.youtube_urls[0] });
    } else if (item.drive_links?.length > 0) {
      setVideoModal({ type: "drive", url: item.drive_links[0] });
    }
    // If only images, Link to /achievements handles navigation
  }

  return (
    <section className="py-20 overflow-hidden" style={{ backgroundColor: "#FAFBFC" }} data-testid="achievements-section">
      {videoModal?.type === "youtube" && <YoutubeModal url={videoModal.url} onClose={() => setVideoModal(null)} />}
      {videoModal?.type === "drive" && <DriveVideoModal url={videoModal.url} onClose={() => setVideoModal(null)} />}

      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}
          className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#4F46E5] mb-2">Achievements</div>
            <h2 className="rebrand-serif text-3xl sm:text-4xl font-bold tracking-tight text-[#1A202C]">
              {heading}
            </h2>
            {description && (
              <p className="mt-2 text-sm text-gray-500 max-w-xl leading-relaxed">{description}</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {items.length > 1 && (
              <>
                <button onClick={scrollPrev} aria-label="Previous"
                  className="w-9 h-9 rounded-full border border-gray-200 bg-white grid place-items-center hover:bg-gray-50 transition">
                  <ChevronLeft className="w-4 h-4 text-gray-600" />
                </button>
                <button onClick={scrollNext} aria-label="Next"
                  className="w-9 h-9 rounded-full border border-gray-200 bg-white grid place-items-center hover:bg-gray-50 transition">
                  <ChevronRight className="w-4 h-4 text-gray-600" />
                </button>
              </>
            )}
            <Link to="/achievements" className="hidden sm:inline-flex items-center gap-1.5 text-sm font-medium text-[#4F46E5] hover:underline ml-2">
              View all <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </motion.div>

        {loading ? (
          <div className="flex gap-5">
            {[1, 2, 3, 4].map(n => <div key={n} className="w-72 h-64 rounded-2xl bg-gray-100 animate-pulse shrink-0" />)}
          </div>
        ) : items.length === 0 ? (
          /* Empty state — section still renders, never returns null */
          <div className="rounded-2xl border-2 border-dashed border-gray-200 py-16 text-center">
            <Star className="w-8 h-8 mx-auto text-gray-300 mb-3" />
            <p className="text-sm text-gray-400">Achievements will appear here once published.</p>
            <Link to="/achievements" className="mt-3 inline-flex items-center gap-1 text-xs text-indigo-500 hover:underline">
              View achievements page <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        ) : (
          <div ref={emblaRef} className="overflow-hidden -mx-2">
            <div className="flex">
              {items.map((item) => {
                const hasVideo = (item.youtube_urls?.length > 0) || (item.drive_links?.length > 0);
                const isClickable = hasVideo;
                return (
                  <div key={item.id} className="min-w-0 shrink-0 px-2" style={{ flexBasis: "calc(100% / 1.15)", maxWidth: 320 }}>
                    {isClickable ? (
                      <button type="button" className="block w-full text-left group" onClick={() => handleCardClick(item)}>
                        <AchievementCard item={item} hasVideo={hasVideo} />
                      </button>
                    ) : (
                      <Link to="/achievements" className="block group">
                        <AchievementCard item={item} hasVideo={hasVideo} />
                      </Link>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="sm:hidden mt-6 text-center">
          <Link to="/achievements" className="inline-flex items-center gap-1.5 text-sm font-medium text-[#4F46E5] hover:underline">
            View all achievements <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    </section>
  );
}

function AchievementCard({ item, hasVideo }) {
  const cover = getCover(item);
  return (
    <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all">
      <div className="relative aspect-[16/10] bg-gray-100 overflow-hidden">
        {cover ? (
          cover.includes("img.youtube.com") ? (
            <YtImg videoId={cover.match(/\/vi\/([^/]+)\//)?.[1]} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          ) : (
            <img src={resolve(cover)} alt={item.title} loading="lazy"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          )
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-300">
            <Star className="w-8 h-8" />
          </div>
        )}
        {hasVideo && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-10 h-10 rounded-full bg-white/90 backdrop-blur grid place-items-center shadow">
              <Play className="w-4 h-4 text-gray-800 ml-0.5" fill="currentColor" />
            </div>
          </div>
        )}
        {item.featured && (
          <div className="absolute top-2 left-2 flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500 text-white text-[9px] font-semibold">
            <Star className="w-2.5 h-2.5" fill="currentColor" /> Featured
          </div>
        )}
      </div>
      <div className="p-3.5">
        <div className="flex items-center gap-1.5 text-[10px] text-gray-400 font-medium uppercase tracking-wider mb-1 flex-wrap">
          <Calendar className="w-3 h-3 shrink-0" />
          {MONTHS[item.achievement_month] || ""} {item.achievement_year}
          {item.category && <><span className="opacity-40">·</span><span className="text-indigo-500">{item.category}</span></>}
        </div>
        <h3 className="text-sm font-semibold text-gray-800 line-clamp-2 leading-snug">{item.title}</h3>
        {item.subtitle && <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{item.subtitle}</p>}
      </div>
    </div>
  );
}
